import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

import { ElMessageBox } from 'element-plus'
import { showDialog } from 'vant';
import qs from 'qs'
import { config } from '@/config/axios/config'
import { md5 } from '@/utils/md5'
import {
  getAccessToken,
  getRefreshToken,
  removeToken,
  setToken
} from './auth'
import errorCode from './errorCode'

import { deleteUserCache } from './useCache'
import { showFailToast } from 'vant'

const { result_code, base_url, request_timeout } = config
const signatureSecret = 'd237090728e085b5745c363638ac968e'

// 需要忽略的提示。忽略后，自动抛给调用方处理
const ignoreMsgs = [
  '无效的刷新令牌', // 刷新令牌被删除时，不用提示
  '刷新令牌已过期' // 使用刷新令牌，刷新获取新的访问令牌时，结果因为过期失败，此时需要忽略。否则，会导致继续 401，无法跳转到登出界面
]
// 是否显示重新登录
export const isRelogin = { show: false }
// Axios 无感知刷新令牌，参考 https://www.dashingdog.cn/article/11 与 https://segmentfault.com/a/1190000020210980 实现
// 请求队列
let requestList: any[] = []
// 是否正在刷新中
let isRefreshToken = false
// 请求白名单，无须 token 的接口
const whiteList: string[] = ['/login', '/register', '/refresh-token', '/validate-reset-password', '/reset-password', '/forgot-username', '/pay-success']

type NotifiedError = Error & { notified?: boolean }

const createNotifiedError = (message: string): NotifiedError => {
  const error = new Error(message) as NotifiedError
  error.notified = true
  return error
}

const rejectWithToast = (message: string, toastMessage = message) => {
  showFailToast(toastMessage)
  return Promise.reject(createNotifiedError(message))
}

const createUuid = () => {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID().replace(/-/g, '')
  }

  const bytes = new Uint8Array(16)
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256)
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'))
  return hex.join('')
}

const trimSlashes = (value: string) => value.replace(/^\/+|\/+$/g, '')

const getRequestPath = (requestConfig: InternalAxiosRequestConfig) => {
  const url = requestConfig.url || ''
  const urlPath = url.split('?')[0]

  if (/^https?:\/\//i.test(urlPath)) {
    return new URL(urlPath).pathname
  }

  const basePath = requestConfig.baseURL
    ? new URL(requestConfig.baseURL, window.location.origin).pathname
    : ''
  const path = [basePath, urlPath].map(trimSlashes).filter(Boolean).join('/')
  return path ? `/${path}` : '/'
}

// 创建axios实例
const service: AxiosInstance = axios.create({
  baseURL: base_url, // api 的 base_url
  timeout: request_timeout, // 请求超时时间
  withCredentials: false, // 禁用 Cookie 等信息
  // 自定义参数序列化函数
  paramsSerializer: (params) => {
    return qs.stringify(params, { allowDots: true })
  }
})

// request拦截器
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 是否需要设置 token；命中白名单的接口（如 /login）不带 token
    const headers = (config.headers || {}) as Record<string, any>
    let isToken = headers.isToken !== false
    if (isToken && whiteList.some((v) => config.url?.includes(v))) {
      isToken = false
    }
    if (getAccessToken() && isToken) {
      config.headers.Authorization = 'Bearer ' + getAccessToken() // 让每个请求携带自定义 token
    }
    const method = config.method?.toUpperCase()
    // 防止 GET 请求缓存
    if (method === 'GET') {
      //config.headers['Cache-Control'] = 'no-cache'
      //config.headers['Pragma'] = 'no-cache'
    }
    // 自定义参数序列化函数
    else if (method === 'POST') {
      const contentType = config.headers['Content-Type'] || config.headers['content-type']
      if (contentType === 'application/x-www-form-urlencoded') {
        if (config.data && typeof config.data !== 'string') {
          config.data = qs.stringify(config.data)
        }
      }
    }
    const timestamp = Date.now().toString()
    const nonce = createUuid()
    const requestPath = getRequestPath(config)
    const signatureContent = `${signatureSecret}|${timestamp}|${nonce}|${requestPath}`
    config.headers['X-Timestamp'] = timestamp
    config.headers['X-Nonce'] = nonce
    config.headers['X-Signature'] = md5(signatureContent).toLowerCase()
    return config
  },
  (error: AxiosError) => {
    // Do something with request error
    console.log(error) // for debug
    return Promise.reject(error)
  }
)

// response 拦截器
service.interceptors.response.use(
  async (response: AxiosResponse<any>) => {
    let { data } = response
    const config = response.config
    if (!data) {
      // 返回“[HTTP]请求没有返回值”;
      return rejectWithToast('[HTTP]请求没有返回值')
    }

    // 未设置状态码则默认成功状态
    // 二进制数据则直接返回，例如说 Excel 导出
    if (
      response.request.responseType === 'blob' ||
      response.request.responseType === 'arraybuffer'
    ) {
      // 注意：如果导出的响应为 json，说明可能失败了，不直接返回进行下载
      if (response.data.type !== 'application/json') {
        return response.data
      }
      data = await new Response(response.data).json()
    }
    const code = data.code ?? result_code
    // 获取错误信息
    const msg = data.msg || errorCode[code] || errorCode['default']
    if (ignoreMsgs.indexOf(msg) !== -1) {
      // 如果是忽略的错误码，直接返回 msg 异常
      return Promise.reject(new Error(msg))
    } else if (code === 401) {
      // 如果未认证，并且未进行刷新令牌，说明可能是访问令牌过期了
      if (!isRefreshToken) {
        isRefreshToken = true
        // 1. 如果获取不到刷新令牌，则只能执行登出操作
        if (!getRefreshToken()) {
          return handleAuthorized()
        }
        // 2. 进行刷新访问令牌
        try {
          const refreshTokenRes = await refreshToken()
          // 2.1 刷新成功，则回放队列的请求 + 当前请求
          setToken((await refreshTokenRes).data.data)
          config.headers!.Authorization = 'Bearer ' + getAccessToken()
          requestList.forEach((cb: any) => {
            cb()
          })
          requestList = []
          return service(config)
        } catch (e) {
          // 为什么需要 catch 异常呢？刷新失败时，请求因为 Promise.reject 触发异常。
          // 2.2 刷新失败，只回放队列的请求
          requestList.forEach((cb: any) => {
            cb()
          })
          // 提示是否要登出。即不回放当前请求！不然会形成递归
          return handleAuthorized()
        } finally {
          requestList = []
          isRefreshToken = false
        }
      } else {
        // 添加到队列，等待刷新获取到新的令牌
        return new Promise((resolve) => {
          requestList.push(() => {
            config.headers!.Authorization = 'Bearer ' + getAccessToken() // 让每个请求携带自定义token 请根据实际情况自行修改
            resolve(service(config))
          })
        })
      }
    } else if (code === 500) {
      return rejectWithToast(msg, '服务器错误,请联系管理员!')
    } else if (code !== 0 && code !== 200) {
      if (msg === '无效的刷新令牌') {
        // hard coding：忽略这个提示，直接登出
        return handleAuthorized()
      }
      return rejectWithToast(msg)
    } else {
      return data
    }
  },
  (error: AxiosError) => {
    let { message } = error
    if (message === 'Network Error') {
      message = '操作失败,系统异常!'
    } else if (message.includes('timeout')) {
      message = '接口请求超时,请刷新页面重试!'
    } else if (message.includes('Request failed with status code')) {
      message = '请求出错，请稍候重试' + message.substr(message.length - 3)
    }
    showFailToast(message)
    const notifiedError = error as AxiosError & { notified?: boolean }
    notifiedError.notified = true
    return Promise.reject(notifiedError)
  }
)

const refreshToken = async () => {
  return await axios.post(base_url + '/system/auth/refresh-token?refreshToken=' + getRefreshToken())
}
const handleAuthorized = () => {
  if (!isRelogin.show) {
    // 如果已经到登录页面则不进行弹窗提示
    if (window.location.href.includes('login')) {
      return
    }
    isRelogin.show = true
    showDialog({title: '系统提示', message: '登录超时,请重新登录!'}).then(() => {
      deleteUserCache() // 删除用户缓存
      removeToken()
      isRelogin.show = false
      // 干掉token后再走一次路由让它过router.beforeEach的校验
      window.location.href = '/login'
    })
  }
  return Promise.reject('登录超时,请重新登录!')
}
export { service }
